import { isUndefined } from 'lodash';
import { extractBulkImportResult } from 'src/components/organization/projects/frs/frsUtils';
import { FrsBulkImportResult } from 'src/components/organization/projects/frs/frsTypes';
import axiosInstance from 'src/utils/axios';
import parse from 'src/utils/parse';

const parseFilenameFromContentDisposition = (
  header: string | undefined
): string | null => {
  if (!header) return null;
  const utf8Match = header.match(/filename\*=(?:UTF-8'')?([^;\s]+)/i);
  if (utf8Match) {
    const raw = utf8Match[1].replace(/['"]/g, '');
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  const asciiMatch = header.match(/filename\s*=\s*("?)([^";\n]+)\1/i);
  return asciiMatch ? asciiMatch[2].trim() : null;
};

const readBlobErrorMessage = async (blob: Blob): Promise<string> => {
  try {
    const text = await blob.text();
    try {
      const json = JSON.parse(text);
      return json.message || text || 'Request failed';
    } catch {
      return text || 'Request failed';
    }
  } catch {
    return 'Request failed';
  }
};

export interface GetFrsHierarchyQuery {
  projectId: string;
}

export interface CreateFrsPayload {
  approvalStatus: boolean;
  categoryId: string;
  description: string;
  gate: string;
  moscow: string;
  projectId: string;
  title: string;
}

export interface GetFrsDetailResponse {
  id: string;
  projectId: string;
  projectName: string;
  categoryId: string;
  categoryName: string;
  categoryPath: Array<number | string>;
  frsPath: Array<number | string>;
  frsDisplayKey: string;
  title: string;
  description: string;
  gate: string;
  moscow: string;
  approvalStatus: boolean | null;
  approvedBy: string | null;
  approvedAt: string | null;
  currentVersion: number;
  isActive: boolean;
  metadata: Record<string, any>;
}

export interface FrsVersionItem {
  id: string;
  frsId: string;
  projectId: string;
  projectName: string;
  categoryName: string;
  versionNumber: number;
  isCurrent: boolean;
  gate: string;
  moscow: string;
  approvalStatus: boolean | null;
  approvedBy: string | null;
  approvedAt: string | null;
  snapshot: Record<string, any>;
  changeFrom: Record<string, any>;
  changeTo: Record<string, any>;
  createdBy: {
    name: string;
    avatar?: string;
    _id?: string;
  };
  createdAt: string;
}

export interface FrsCaseItem {
  _id: string;
  id: string;
  caseKey: string;
  summary: string;
  description: string;
  createdAt: string;
  createdBy?: {
    name: string;
  };
  priority?: {
    name: string;
  };
  status?: {
    name: string;
  };
  frsId: string;
}

class FrsApi {
  async getFrsHierarchy(query: GetFrsHierarchyQuery): Promise<any> {
    return new Promise((resolve, reject) => {
      axiosInstance
        .get(`${process.env.REACT_APP_CASES}/frs/hierarchy`, {
          params: query,
        })
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async createFrs(payload: CreateFrsPayload): Promise<any> {
    return new Promise((resolve, reject) => {
      axiosInstance
        .post(`${process.env.REACT_APP_CASES}/frs`, payload)
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async updateFrs(id: string, payload: Partial<CreateFrsPayload>): Promise<any> {
    return new Promise((resolve, reject) => {
      axiosInstance
        .put(`${process.env.REACT_APP_CASES}/frs/${id}`, payload)
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getFrsDetails(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      axiosInstance
        .get(`${process.env.REACT_APP_CASES}/frs/${id}`)
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getFrsVersions(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      axiosInstance
        .get(`${process.env.REACT_APP_CASES}/frs/${id}/versions`)
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  async getFrsCases(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      axiosInstance
        .get(`${process.env.REACT_APP_CASES}/frs/${id}/cases`)
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('internal Server Error'));
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  private async downloadBlob(
    url: string,
    params: Record<string, string>,
    defaultFilename: string,
    acceptHeader?: string
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await axiosInstance.get(url, {
      params,
      responseType: 'blob',
      headers: acceptHeader ? { Accept: acceptHeader } : undefined,
    });

    if (response.status < 200 || response.status >= 300 || !response.data) {
      const message = await readBlobErrorMessage(response.data as Blob);
      throw new Error(message);
    }

    const contentType = String(response.headers['content-type'] || '');
    if (contentType.includes('application/json')) {
      const message = await readBlobErrorMessage(response.data as Blob);
      throw new Error(message);
    }

    const filename =
      parseFilenameFromContentDisposition(
        response.headers['content-disposition'] as string | undefined
      ) || defaultFilename;

    return { blob: response.data as Blob, filename };
  }

  async downloadBulkImportTemplate(
    projectId: string
  ): Promise<{ blob: Blob; filename: string }> {
    return this.downloadBlob(
      `${process.env.REACT_APP_CASES}/frs/bulk-import/template`,
      { projectId },
      'frs_bulk_template.xlsx'
    );
  }

  async exportBulkImport(projectId: string): Promise<{ blob: Blob; filename: string }> {
    return this.downloadBlob(
      `${process.env.REACT_APP_CASES}/frs/bulk-import/export`,
      { projectId },
      'frs_export.xlsx',
      'application/octet-stream'
    );
  }

  async bulkImport(projectId: string, file: File): Promise<FrsBulkImportResult> {
    const formData = new FormData();
    formData.append('projectId', projectId);
    formData.append('file', file);
    formData.append('fileName', file.name);

    return new Promise((resolve, reject) => {
      axiosInstance
        .post(`${process.env.REACT_APP_CASES}/frs/bulk-import`, formData, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((response) => {
          const parsed = parse(response);
          if (!isUndefined(parsed?.data)) {
            resolve(extractBulkImportResult(parsed));
          } else {
            reject(new Error('Bulk import failed'));
          }
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            'Bulk import failed';
          reject(new Error(message));
        });
    });
  }
}

export const frsApi = new FrsApi();